"use client";

import Link from "next/link";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import { SignedIn, SignedOut } from "@clerk/nextjs";
import { HeroSection } from "@/components/landing/hero-section";
import { FeatureGrid } from "@/components/landing/feature-grid";
import { HowItThinks } from "@/components/landing/how-it-thinks";
import { RepoMemorySection } from "@/components/landing/repo-memory-section";
import { CodeReviewPreview } from "@/components/landing/code-review-preview";
import { IntegrationShowcase } from "@/components/landing/integration-showcase";
import { GithubShowcase } from "@/components/landing/github-showcase";
import { FAQSection } from "@/components/landing/faq-section";
import { FinalCTA } from "@/components/landing/final-cta";
import { ThemeToggle } from "@/components/ui/theme-toggle";

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-background text-foreground relative selection:bg-primary selection:text-primary-foreground">

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-border/40 bg-background/80 backdrop-blur-md">
        <div className="container mx-auto flex h-16 items-center justify-between px-4">
          <Link href="/" className="flex items-center gap-2">
            <Image src="/LOGO.png" alt="Burg AI" width={32} height={32} className="w-8 h-8 rounded-lg object-cover" />
            <h1 className="text-lg font-semibold tracking-tight">burg ai</h1>
          </Link>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Link href="/docs">
              <Button variant="ghost" className="text-muted-foreground hover:text-foreground text-sm">
                docs
              </Button>
            </Link>
            <SignedIn>
              <Link href="/dashboard">
                <Button variant="ghost" className="text-muted-foreground cursor-pointer hover:text-foreground text-sm">
                  dashboard
                </Button>
              </Link>
            </SignedIn>
            <SignedOut>
              <Link href="/sign-in">
                <Button variant="ghost" className="text-muted-foreground hover:text-foreground text-sm">
                  sign in
                </Button>
              </Link>
              <Link href="/sign-up">
                <Button className="bg-foreground text-background hover:bg-foreground/90 font-medium rounded-lg text-sm">
                  get started
                </Button>
              </Link>
            </SignedOut>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10">
        <HeroSection />
        <FeatureGrid />
        <HowItThinks />
        <RepoMemorySection />
        <CodeReviewPreview />
        <IntegrationShowcase />
        <GithubShowcase />
        <FAQSection />
        <FinalCTA />
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-8 lg:gap-12 mb-12">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-6">
                <Image src="/LOGO.png" alt="Burg AI" width={32} height={32} className="w-8 h-8 rounded-lg object-cover" />
                <span className="text-lg font-semibold">burg ai</span>
              </div>
              <p className="text-muted-foreground text-sm max-w-sm">
                ai-powered code reviews for modern engineering teams.
              </p>
            </div>

            <div>
              <h3 className="font-medium mb-4 text-sm">product</h3>
              <ul className="space-y-3 text-sm text-muted-foreground">
                <li><Link href="/docs" className="hover:text-foreground transition-colors">docs</Link></li>
                <li><Link href="#" className="hover:text-foreground transition-colors">features</Link></li>
                <li><Link href="#" className="hover:text-foreground transition-colors">integrations</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="font-medium mb-4 text-sm">resources</h3>
              <ul className="space-y-3 text-sm text-muted-foreground">
                <li><Link href="#" className="hover:text-foreground transition-colors">pricing</Link></li>
                <li><Link href="#" className="hover:text-foreground transition-colors">changelog</Link></li>
                <li><Link href="https://statuspage.incident.io/burg-ai-status" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">status</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="font-medium mb-4 text-sm">legal</h3>
              <ul className="space-y-3 text-sm text-muted-foreground">
                <li><Link href="#" className="hover:text-foreground transition-colors">privacy</Link></li>
                <li><Link href="#" className="hover:text-foreground transition-colors">terms</Link></li>
                <li><Link href="#" className="hover:text-foreground transition-colors">security</Link></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-border flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-muted-foreground">
            <p>&copy; {new Date().getFullYear()} burg ai. all rights reserved.</p>
            <p>built for developers.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

